import { Navigate, Route, Routes } from 'react-router-dom';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { VerificaIdentitaPage } from '@/pages/VerificaIdentitaPage';
import { RegistrationLayout } from '@/pages/RegistrationLayout';
import { OrganizationPage } from '@/pages/steps/OrganizationPage';
import { ContattiPage } from '@/pages/steps/ContattiPage';
import { IndirizziPage } from '@/pages/steps/IndirizziPage';
import { AdminPage } from '@/pages/steps/AdminPage';
import { AccountPage } from '@/pages/steps/AccountPage';
import { FiscalePage } from '@/pages/steps/FiscalePage';
import { SummaryPage } from '@/pages/steps/SummaryPage';
import { SuccessPage } from '@/pages/SuccessPage';
import { AreaLayout } from '@/pages/area/AreaLayout';
import { AreaLoginPage } from '@/pages/area/AreaLoginPage';
import { AreaStatusPage } from '@/pages/area/AreaStatusPage';
import { AreaDataPage } from '@/pages/area/AreaDataPage';
import { AreaDocumentsPage } from '@/pages/area/AreaDocumentsPage';
import { AreaIdentityPage } from '@/pages/area/AreaPlaceholderPages';
import { resumableAreaEnabled } from '@/lib/env';

const ROOT = '/registrazione-intermediario';

/**
 * Route grammar (PR-B — new 6-step structure):
 *   /registrazione-intermediario           → wizard layout (form context)
 *     /organizzazione                      Step 1 — organization fundamentals
 *     /contatti                            Step 2 — contact channels (placeholder, PR-C fills)
 *     /indirizzi                           Step 3 — addresses (single sede legale today, multi-address in PR-C)
 *     /amministratore                      Step 4 — admin / signatory user (sex+dateOfBirth+birthCountry)
 *     /fiscale                             Step 5 — withholding regime (placeholder, PR-D fills)
 *     /riepilogo                           Step 6 — recap + Turnstile + submit
 *     /verifica-identita                   Post-submit identity verification (KYC) step (DIDIT-6B)
 *     /documenti                           Post-submit document upload
 *     /success                             Terminal
 *
 * Legacy URL redirects:
 *   /admin     → /amministratore
 *   /sede      → /indirizzi
 *   These keep the dev wizard accessible to anyone who bookmarked the old
 *   URLs and lets us delete the redirects in PR-C/PR-D once the new
 *   surface is the only one shipping. They live under the same
 *   `RegistrationLayout` so the FormProvider context survives the
 *   redirect.
 *
 * `documenti` and `success` live under the same parent route so the URL
 * prefix is uniform; they don't read form context, which is harmless.
 */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path={ROOT} element={<RegistrationLayout />}>
        <Route index element={<Navigate to="organizzazione" replace />} />
        <Route path="organizzazione" element={<OrganizationPage />} />
        <Route path="contatti" element={<ContattiPage />} />
        <Route path="indirizzi" element={<IndirizziPage />} />
        <Route path="amministratore" element={<AdminPage />} />
        {/* PR-ONB-RESUME-1B — account step (Firebase signup client-side +
            authenticated bind). Mounted ONLY behind the FE flag so the
            legacy anonymous wizard stays byte-identical when off. */}
        {resumableAreaEnabled ? (
          <Route path="account" element={<AccountPage />} />
        ) : null}
        <Route path="fiscale" element={<FiscalePage />} />
        <Route path="riepilogo" element={<SummaryPage />} />
        <Route path="verifica-identita" element={<VerificaIdentitaPage />} />
        <Route path="documenti" element={<DocumentsPage />} />
        <Route path="success" element={<SuccessPage />} />

        {/* Legacy URL redirects — pre-PR-B link grammar. */}
        <Route
          path="admin"
          element={<Navigate to="../amministratore" replace />}
        />
        <Route
          path="sede"
          element={<Navigate to="../indirizzi" replace />}
        />
      </Route>

      {/* PR-ONB-RESUME-1B — resumable mini-area (lock #6: Dati / Documenti /
          Verifica identità / Stato richiesta, nothing else). Flag-gated. */}
      {resumableAreaEnabled ? (
        <>
          <Route path="/area/login" element={<AreaLoginPage />} />
          <Route path="/area" element={<AreaLayout />}>
            <Route index element={<Navigate to="stato" replace />} />
            <Route path="dati" element={<AreaDataPage />} />
            <Route path="documenti" element={<AreaDocumentsPage />} />
            <Route path="verifica-identita" element={<AreaIdentityPage />} />
            <Route path="stato" element={<AreaStatusPage />} />
          </Route>
        </>
      ) : null}

      <Route path="*" element={<Navigate to={ROOT} replace />} />
    </Routes>
  );
}
